"use client";
import React, { useEffect, useMemo } from "react";
import Icon from "@/components/icons/Icons";
import { allSwpCalculation } from "@/utils/allCalculators";
import { BucketCardConfig, RetirementSummaryValues } from "@/type/financialPageTypes";
import { getBucketAllocation, POST_RETIREMENT_PENSION_RATE } from "@/utils/financialPageFunctions";
import { useRetirementBuckets } from "../hooks/useRetirementBuckets";
import RetirementBucketGrid, { fmtINR } from "../ui/RetirementBucketGrid";
import RetirementChart from "../charts/RetirementChart";

export default function SummaryPostRetirement({
  corpus,
  retirementDuration,
  onSummaryChangeAction,
}: {
  corpus: number;
  retirementDuration: number;
  onSummaryChangeAction?: (values: RetirementSummaryValues) => void;
}) {
  const bucketAllocations = getBucketAllocation(corpus);

  const {
    pensionBucket,
    healthBucket,
    vacationBucket,
    legacyBucket,
    pensionMonthly,
    healthGrows,
    vacationAnnual,
    legacyGrows,
  } = useRetirementBuckets(corpus, retirementDuration, bucketAllocations);

  const swpPension = useMemo(
    () => allSwpCalculation(pensionBucket, pensionMonthly, retirementDuration, POST_RETIREMENT_PENSION_RATE, "Monthly"),
    [corpus, pensionMonthly, retirementDuration],
  );

  useEffect(() => {
    if (corpus <= 0) return;
    onSummaryChangeAction?.({
      corpus,
      sipValue: corpus,
      expectedReturnRate: POST_RETIREMENT_PENSION_RATE,
      tenure: retirementDuration,
    });
  }, [corpus, retirementDuration, onSummaryChangeAction]);

  if (corpus <= 0) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6 text-center">
        <p className="text-[#d70000] font-semibold">Complete the previous steps to view your retirement summary.</p>
      </div>
    );
  }

  const bucketCards: BucketCardConfig[] = [
    {
      title: "Pension Bucket",
      dotColor: "#FDE006",
      value: pensionBucket,
      secondaryValue: pensionMonthly,
      prefix: <Icon.rightArrow />,
      suffix: "/ M",
    },
    {
      title: "Health Reserve",
      dotColor: "#79FD06",
      value: healthBucket,
      secondaryValue: healthGrows,
      prefix: "Grows to",
      suffix: "",
    },
    {
      title: "Vacation",
      dotColor: "#04BCFF",
      value: vacationBucket,
      secondaryValue: vacationAnnual,
      prefix: <Icon.rightArrow />,
      suffix: "/ Yr",
    },
    {
      title: "Legacy",
      dotColor: "#FF9304",
      value: legacyBucket,
      secondaryValue: legacyGrows,
      prefix: "Grows to",
      suffix: "",
    },
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 md:gap-6 h-full overflow-x-auto scrollbar-none sm:overflow-hidden">
        {/* Left: corpus + monthly pension + bucket grid */}
        <div className="flex flex-col rounded-2xl md:rounded-6xl p-4 xl:p-5 text-white shrink-0 w-full h-auto sm:h-full sm:max-w-90 md:max-w-96 bg-cover bg-center summary-step-financial">
          <p className="text-sm font-medium text-white/60 mb-1">Your Retirement Corpus</p>
          <div className="text-xl xl:text-2xl font-bold text-white mb-3 sm:mb-4">{fmtINR(corpus)}</div>

          <p className="text-sm font-medium text-white/60 mb-1">Pension Income</p>
          <div className="text-xl xl:text-2xl font-bold text-white mb-3 sm:mb-4">{fmtINR(pensionMonthly)} /M</div>

          <div className="pt-2.5 xs:px-2.5 xs:py-3 sm:p-4 xs:rounded-xl sm:rounded-2xl border-t border-dashed xs:border-solid xs:border border-white/25 xs:bg-white/15 xs:backdrop-blur-3xl">
            <p className="text-2xs sm:text-sm sm:leading-5 leading-1.2 font-medium text-white text-center mb-3 tracking-[-3%]">
              Here&apos;s your Corpus utilisation for Post Retirement ({retirementDuration} Years)
            </p>

            <RetirementBucketGrid buckets={bucketCards} isPostSummary={true} />
          </div>
        </div>

        {/* Right: SWP Summary + chart */}
        <div className="flex flex-col gap-4 sm:gap-5 flex-1">
          <div className="text-sm sm:text-base border border-gray-200 rounded-xl sm:rounded-2xl md:rounded-6xl p-4 sm:p-5 xl:px-6">
            <div className="flex justify-between items-center pb-2.5 mb-2.5 sm:pb-3 sm:mb-3 border-b border-dashed border-[#D9D9D9]">
              <span className="font-medium text-black">SWP Summary</span>
              <span className="text-black/60">Value</span>
            </div>
            <div className="space-y-2.5 sm:space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-black/60">Retirement Corpus</span>
                <span className="font-semibold text-black">{fmtINR(corpus)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-black/60">Monthly Withdrawal</span>
                <span className="font-bold text-primaryBlue">{fmtINR(pensionMonthly)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-black/60">Duration</span>
                <span className="font-semibold text-black">{retirementDuration} years</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-black/60">Total Withdrawn</span>
                <span className="font-semibold text-[#158700]">{fmtINR(swpPension.totalWithdrawn)}</span>
              </div>
            </div>
          </div>

          <div className="flex-1 bg-[#E1E8F3] p-3 rounded-2xl sm:rounded-4xl">
            <RetirementChart data={swpPension.reportData} />

            <div className="border-t border-dashed border-black/20 pt-2">
              <p className="text-xs font-medium text-black/80 text-center">
                Corpus depletion over {retirementDuration} years
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-[10px] text-black/30 text-center pb-2">
        <b>Disclaimer:</b> Calculations and projected fund returns are based on the historical performance of mutual funds. Actual investment returns depend on market conditions and may vary over time.
      </p>
    </div>
  );
}
